import React, { useMemo } from 'react';
import { Appointment } from '../../types';
import {
  buildUpcomingScheduleItems,
  groupUpcomingScheduleByDay,
} from './dashboardUtils';
import { useEvents } from '../../contexts/EventsContext';
import { getUpcomingEvents, getEventDisplayStyles } from '../Calendar/eventUtils';
import { t } from '../../utils/translations';

interface AppointmentListProps {
  appointments: Appointment[];
  loading?: boolean;
  limit?: number;
  onViewCalendar: () => void;
  /** No card chrome — sits inside QuickPanel or on the page. */
  embedded?: boolean;
  /** Put „Vezi calendar” in the header row (mockup card). */
  calendarLinkInHeader?: boolean;
}

const AppointmentList: React.FC<AppointmentListProps> = ({
  appointments,
  loading,
  limit = 6,
  onViewCalendar,
  embedded = false,
  calendarLinkInHeader = false,
}) => {
  const { events, loading: eventsLoading } = useEvents();
  const eventStyles = getEventDisplayStyles();

  const upcomingItems = useMemo(() => {
    const upcomingEvents = getUpcomingEvents(events);
    return buildUpcomingScheduleItems(appointments, upcomingEvents);
  }, [appointments, events]);

  const limited = upcomingItems.slice(0, limit);
  const groups = groupUpcomingScheduleByDay(limited);
  const isLoading = loading || eventsLoading;

  if (embedded) {
    return (
      <section>
        <div className="mb-4">
          <h2 className="text-base font-semibold text-slate-900">
            {t.dashboard.todayFocus.myAppointments}
          </h2>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-4 animate-pulse rounded bg-slate-100" />
            ))}
          </div>
        ) : limited.length === 0 ? (
          <p className="text-sm text-slate-500">{t.dashboard.noAppointments}</p>
        ) : (
          <div className="space-y-4">
            {groups.map((group, groupIndex) => (
              <div
                key={group.label}
                className={
                  groupIndex > 0 ? 'border-t border-slate-100 pt-4' : undefined
                }
              >
                <p className="mb-2 text-xs font-medium text-slate-400">{group.label}</p>
                <ul className="space-y-2.5">
                  {group.items.map((item) => (
                    <li
                      key={`${item.kind}-${item.id}-${item.date.toISOString()}`}
                      className="flex items-baseline gap-2 text-sm"
                    >
                      {item.kind === 'event' ? (
                        <>
                          <span className="shrink-0 font-semibold text-slate-800">
                            {item.startTime}
                          </span>
                          <span className="text-slate-400" aria-hidden>
                            ·
                          </span>
                          <span className="flex min-w-0 flex-1 items-center gap-2 text-slate-600">
                            <span
                              className={`inline-block h-2 w-2 shrink-0 rounded-full ${eventStyles.dot}`}
                              aria-hidden
                            />
                            <span className="truncate">{item.label}</span>
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="shrink-0 font-semibold text-slate-800">
                            {item.startTime}
                          </span>
                          <span className="text-slate-400" aria-hidden>
                            ·
                          </span>
                          <span className="min-w-0 flex-1 truncate text-slate-600">
                            {item.label}
                          </span>
                          {item.sessionLabel ? (
                            <span className="ml-3 shrink-0 whitespace-nowrap text-xs text-slate-400">
                              {item.sessionLabel}
                            </span>
                          ) : null}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {!calendarLinkInHeader && (
          <button
            type="button"
            onClick={onViewCalendar}
            className="mt-4 text-xs font-medium text-brand-600 hover:text-brand-700"
          >
            Vezi calendar →
          </button>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">{t.dashboard.upcomingAppointments}</h2>
        <button
          type="button"
          onClick={onViewCalendar}
          className="text-xs font-medium text-brand-600 hover:text-brand-700"
        >
          Vezi calendar
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-4 animate-pulse rounded bg-slate-100" />
          ))}
        </div>
      ) : limited.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-500">{t.dashboard.noAppointments}</p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.label}>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">
                {group.label}
              </p>
              <ul className="space-y-2">
                {group.items.map((item) => (
                  <li
                    key={`${item.kind}-${item.id}-${item.date.toISOString()}`}
                    className="flex items-center gap-3 text-sm"
                  >
                    <span className="w-12 shrink-0 font-medium text-slate-700">
                      {item.startTime}
                    </span>
                    {item.kind === 'event' ? (
                      <span className="flex items-center gap-2 text-slate-600">
                        <span
                          className={`inline-block h-2 w-2 shrink-0 rounded-full ${eventStyles.dot}`}
                          aria-hidden
                        />
                        <span>{item.label}</span>
                        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700">
                          {t.events.legend}
                        </span>
                      </span>
                    ) : (
                      <span className="text-slate-600">{item.label}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default AppointmentList;
